import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card, Button, Form, Input, Select, Radio, Row, Col,
  InputNumber, Tooltip, Typography, Divider, Modal, Tag, Space, message
} from 'antd';
import { InfoCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const PRODUCTS = [
  { value: 'PROD_Q1', label: '智能摄像机Q1' },
  { value: 'PROD_P1', label: '云台机P1' },
  { value: 'PROD_F1', label: '烟雾报警器F1' },
  { value: 'PROD_S1', label: '智能门锁S1' },
  { value: 'PROD_CAM2', label: '户外摄像机C2' },
];

const GROUPS = [
  { value: 'default', label: '默认分组' },
  { value: 'groupA', label: 'A组' },
  { value: 'groupB', label: 'B组' },
];

const MOCK_ORDERS = {
  WO20260401001: {
    orderName: '摄像机Q1-批次001', tool: 'platform', product: 'PROD_Q1',
    authRule: 'HMY', authCount: 10000,
    prodFwLabel: '主控固件 / V1.0.0',
    testItems: ['图像测试', 'WIFI信号', '麦克风测试'],
    fwConfig: [
      { label: '硬件类型', value: '摇头机' },
      { label: '光源类型', value: '红外' },
      { label: '分辨率', value: '1080P' },
      { label: '语言', value: '中文' },
    ],
    otaTest: true, otaVersion: 'V1.1.0', otaFileName: 'main_fw_v1.1.0_ota.bin',
    writeMode: 'tool', group: 'default',
  },
  WO20260402003: {
    orderName: '云台机P1-批次003', tool: 'platform', product: 'PROD_P1',
    authRule: 'HMY', authCount: 5000,
    prodFwLabel: '主控固件 / V2.0.0',
    testItems: ['图像测试', 'PTZ测试'],
    fwConfig: [],
    otaTest: false,
    writeMode: 'tool', group: 'default',
  },
  WO20260403005: {
    orderName: '报警器F1-外协', tool: 'non-platform', product: 'PROD_F1',
    authRule: 'HMY', authCount: 2000,
  },
  WO20260401002: {
    orderName: '摄像机Q1-批次002', tool: 'platform', product: 'PROD_Q1',
    authRule: 'HMY', authCount: 8000,
    prodFwLabel: 'WiFi模组固件 / V1.2.0',
    testItems: ['图像测试', 'WIFI信号', '电池测试', '蓝牙测试'],
    fwConfig: [
      { label: '网口配置', value: '无线' },
      { label: '语言', value: '多语言' },
    ],
    otaTest: false,
    writeMode: 'scanner', printPwd: '123456', group: 'default',
  },
};

const EditOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('id');
  const [form] = Form.useForm();
  const [orderData, setOrderData] = useState(null);
  const [writeMode, setWriteMode] = useState('tool');
  const [fwConfigVisible, setFwConfigVisible] = useState(false);

  useEffect(() => {
    const data = MOCK_ORDERS[orderId];
    if (!data) return;
    setOrderData(data);
    setWriteMode(data.writeMode || 'tool');
    form.setFieldsValue({
      orderName: data.orderName,
      group: data.group,
      writeMode: data.writeMode,
      printPwd: data.printPwd,
    });
  }, [orderId, form]);

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      message.success('工单已保存');
      navigate('/production/productionOrder');
    } catch { /* validation failed */ }
  };

  if (!orderData) return null;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate('/production/productionOrder')} />
        <Title level={4} style={{ margin: 0 }}>编辑工单</Title>
        <Text type="secondary">{orderId}</Text>
      </div>

      <Card bordered={false} style={{ maxWidth: 960 }}>
        <Form form={form} layout="vertical">

          <Divider orientation="left" plain>基本信息</Divider>
          <Form.Item name="orderName" label="工单名称" rules={[{ required: true, message: '请输入工单名称' }]}>
            <Input placeholder="请输入工单名称" />
          </Form.Item>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="生产工具">
                <Radio.Group value={orderData.tool} disabled>
                  <Radio value="platform">平台产测</Radio>
                  <Radio value="non-platform">非平台产测</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="生产产品">
                <Select value={orderData.product} disabled options={PRODUCTS} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>生产授权</Divider>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="生成规则">
                <Input value={orderData.authRule} disabled />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="添加授权（生产数量）">
                <InputNumber value={orderData.authCount} disabled style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {orderData.tool === 'platform' && (
            <>
              <Divider orientation="left" plain>产测配置</Divider>

              <Row gutter={24} align="top">
                <Col span={12}>
                  <Form.Item label={<div>生产固件<br /><Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>产测工具检测设备固件与生产固件版本不一致，则进行生产固件烧录</Text></div>}>
                    <Text>{orderData.prodFwLabel}</Text>
                  </Form.Item>
                </Col>
                {orderData.fwConfig && orderData.fwConfig.length > 0 && (
                  <Col span={12}>
                    <Form.Item label={<div>固件配置<br /><Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>固件TF卡配置</Text></div>}>
                      <Button type="link" style={{ padding: 0 }} onClick={() => setFwConfigVisible(true)}>查看配置</Button>
                    </Form.Item>
                  </Col>
                )}
              </Row>

              <Form.Item label="生产测试项">
                <Space size={[4, 8]} wrap>{orderData.testItems.map(item => <Tag key={item}>{item}</Tag>)}</Space>
              </Form.Item>

              {orderData.otaTest && (
                <>
                </>
              )}

              <Divider orientation="left" plain>其他配置</Divider>
              <Row gutter={24}>
                <Col span={8}>
                  <Form.Item name="group" label="产测分组（分配生产）">
                    <Select placeholder="请选择分组" options={GROUPS} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="writeMode" label={<span>写号方式 <Tooltip title="推荐产测工具烧录授权码，扫码枪需提前批量打标"><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip></span>}>
                    <Radio.Group onChange={(e) => setWriteMode(e.target.value)}>
                      <Radio value="tool">工具写号</Radio>
                      <Radio value="scanner">扫码枪</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                {writeMode === 'scanner' && (
                  <Col span={8}>
                    <Form.Item name="printPwd" label="打标密码" rules={[{ required: true, message: '请输入打标密码' }]} extra="防止生产人员滥打印标签">
                      <Input.Password placeholder="请输入打标密码" />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </>
          )}

          <Divider />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => navigate('/production/productionOrder')}>取消</Button>
            <Button type="primary" onClick={handleSubmit}>保存</Button>
          </div>
        </Form>
      </Card>

      {/* 固件配置 - 只读文本弹窗 */}
      <Modal title="固件配置" open={fwConfigVisible} onCancel={() => setFwConfigVisible(false)} destroyOnClose width={600}
        footer={<Button onClick={() => setFwConfigVisible(false)}>关闭</Button>}>
        {orderData.fwConfig && (
          <div style={{ marginTop: 8 }}>
            {orderData.fwConfig.map((item, i) => (
              <div key={i} style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Text type="secondary" style={{ width: 180, flexShrink: 0 }}>{item.label}</Text>
                <Text>{item.value}</Text>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EditOrder;
